import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { I18nProvider } from "./context/I18nContext";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import AddressBook from "./pages/AddressBook";
import Items from "./pages/Items";
import Cardex from "./pages/Cardex";
import Requisitions from "./pages/Requisitions";
import PurchaseOrders from "./pages/PurchaseOrders";
import Receipts from "./pages/Receipts";
import Match from "./pages/Match";
import Settings from "./pages/Settings";
import Locations from "./pages/Locations";
import Roles from "./pages/Roles";
import Transfers from "./pages/Transfers";
import CycleCounts from "./pages/CycleCounts";
import Quality from "./pages/Quality";
import Scan from "./pages/Scan";
import Portal from "./pages/Portal";
import Shipments from "./pages/Shipments";
import Serials from "./pages/Serials";
import Mrp from "./pages/Mrp";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import Tutorial from "./pages/Tutorial";
import Accounting from "./pages/Accounting";
import AccountingReports from "./pages/AccountingReports";
import Onboarding from "./pages/Onboarding";
import Splash from "./pages/Splash";
import DesignPreview from "./pages/DesignPreview";
import PoConfirm from "./pages/PoConfirm";
import Classifications from "./pages/Classifications";
import Equipment from "./pages/Equipment";
import MntDashboard from "./pages/MntDashboard";
import MntNotifications from "./pages/MntNotifications";
import MntWorkOrders from "./pages/MntWorkOrders";
import MntWorkOrderDetail from "./pages/MntWorkOrderDetail";
import MntMeters from "./pages/MntMeters";
import MntHistory from "./pages/MntHistory";
import MntFailureCodes from "./pages/MntFailureCodes";
import MntTechnicians from "./pages/MntTechnicians";
import MntInternalOrder from "./pages/MntInternalOrder";
import MntFailureLog from "./pages/MntFailureLog";
import Reabasto from "./pages/Reabasto";
import ConsumptionByArea from "./pages/ConsumptionByArea";
import Personnel from "./pages/Personnel";
import Departments from "./pages/Departments";
import Vales from "./pages/Vales";
import Inbox from "./pages/Inbox";
import MntPMTemplates from "./pages/MntPMTemplates";
import StockRotation from "./pages/StockRotation";
import Dispatch from "./pages/Dispatch";
import InternalRequisitions from "./pages/InternalRequisitions";

const splashSeen = () => {
  try { return sessionStorage.getItem("mfis_splash_seen") === "1"; } catch (e) { return true; }
};

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <div className="min-h-screen flex items-center justify-center text-xs text-muted-foreground">...</div>;
  if (user === false) return <Navigate to={splashSeen() ? "/login" : "/splash"} replace />;
  return children;
}

function InternalOnly({ children }) {
  const { user } = useAuth();
  if (user && user.role === "supplier") return <Navigate to="/portal" replace />;
  return children;
}

function SupplierOnly({ children }) {
  const { user } = useAuth();
  if (user && user.role !== "supplier") return <Navigate to="/" replace />;
  return children;
}

export default function App() {
  return (
    <I18nProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/splash" element={<Splash />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/design-preview" element={<DesignPreview />} />
            <Route path="/po-confirm/:token" element={<PoConfirm />} />
            <Route path="/portal" element={<Protected><SupplierOnly><Portal /></SupplierOnly></Protected>} />
            <Route element={<Protected><InternalOnly><Layout /></InternalOnly></Protected>}>
              <Route path="/" element={<Dashboard />} />
              <Route path="/address-book" element={<AddressBook />} />
              <Route path="/roles" element={<Roles />} />
              <Route path="/items" element={<Items />} />
              <Route path="/classifications" element={<Classifications />} />
              <Route path="/equipment" element={<Equipment />} />
              <Route path="/cardex" element={<Cardex />} />
              <Route path="/stock-rotation" element={<StockRotation />} />
              <Route path="/mnt/dispatch" element={<Dispatch />} />
              <Route path="/internal-requisitions" element={<InternalRequisitions />} />
              <Route path="/requisitions" element={<Requisitions />} />
              <Route path="/purchase-orders" element={<PurchaseOrders />} />
              <Route path="/receipts" element={<Receipts />} />
              <Route path="/match" element={<Match />} />
              <Route path="/invoices" element={<Match />} />
              <Route path="/locations" element={<Locations />} />
              <Route path="/transfers" element={<Transfers />} />
              <Route path="/vales" element={<Vales />} />
              <Route path="/cycle-counts" element={<CycleCounts />} />
              <Route path="/quality" element={<Quality />} />
              <Route path="/shipments" element={<Shipments />} />
              <Route path="/serials" element={<Serials />} />
              <Route path="/mrp" element={<Mrp />} />
              <Route path="/scan" element={<Scan />} />
              <Route path="/mnt/dashboard" element={<MntDashboard />} />
              <Route path="/mnt/notifications" element={<MntNotifications />} />
              <Route path="/mnt/pm-templates" element={<MntPMTemplates />} />
              <Route path="/mnt/work-orders" element={<MntWorkOrders />} />
              <Route path="/mnt/work-orders/:id" element={<MntWorkOrderDetail />} />
              <Route path="/mnt/meters" element={<MntMeters />} />
              <Route path="/mnt/history" element={<MntHistory />} />
              <Route path="/mnt/failure-codes" element={<MntFailureCodes />} />
              <Route path="/mnt/failure-log" element={<MntFailureLog />} />
              <Route path="/mnt/technicians" element={<MntTechnicians />} />
              <Route path="/mnt/equipment/:eid/internal-order" element={<MntInternalOrder />} />
              <Route path="/reabasto" element={<Reabasto />} />
              <Route path="/reports/consumption-by-area" element={<ConsumptionByArea />} />
              <Route path="/personnel" element={<Personnel />} />
              <Route path="/departments" element={<Departments />} />
              <Route path="/inbox" element={<Inbox />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/tutorial" element={<Tutorial />} />
              <Route path="/accounting" element={<Accounting />} />
              <Route path="/accounting/reports" element={<AccountingReports />} />
              <Route path="/onboarding" element={<Onboarding />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </I18nProvider>
  );
}
